// The home-COUNTRY gate.
//
// Casting restricts submissions by country, never by city: an actor who lives
// in Toronto is welcome to submit to a Vancouver breakdown, and blocking them
// would be wrong. Only a country difference is a gate — see docs/16.
//
// Two consequences of that rule, both deliberate:
//   * A breakdown casting in several cities is compared against EVERY city's
//     country, so a multi-city posting cannot false-positive on cities[0].
//   * When either side's country is unknown the answer is "no mismatch". The
//     visitor's country is an IP guess; a lookup that failed must never gate a
//     submission.

import { IBreakdownCityDto, ISubmissionGeoGuessDto } from "../../../shared/api/submission/types";

export interface IGeoMismatch {
  // Country the visitor appears to be in, e.g. "MX".
  userCountry: string;
  // Best label for where they are — city when we have one, else the country.
  userLabel: string;
  // The breakdown's country, and the city to name in the copy.
  breakdownCountry: string;
  breakdownCityName: string;
}

const upper = (value?: string | null) => (value || "").trim().toUpperCase();

interface IBreakdownLike {
  cities: IBreakdownCityDto[];
}

// Returns null when there is nothing to gate on, otherwise the labels the sheet
// needs. Never throws on partial data — every field of both DTOs is optional in
// practice (the real country lookup returns no city at all today).
export const evaluateGeoMismatch = (
  geo: ISubmissionGeoGuessDto | null | undefined,
  info: IBreakdownLike | null | undefined
): IGeoMismatch | null => {
  const userCountry = upper(geo && geo.countryCode);
  if (!userCountry) return null;
  if (!info || !info.cities || !info.cities.length) return null;

  const countries: string[] = [];
  info.cities.forEach((city) => {
    const code = upper(city && city.countryCode);
    if (code && countries.indexOf(code) < 0) countries.push(code);
  });
  if (!countries.length) return null;

  // Same country as ANY of the breakdown's cities — nothing to say.
  if (countries.indexOf(userCountry) > -1) return null;

  // Name the first city of the breakdown's primary country in the copy
  // ("only seeing submissions from Vancouver for this one").
  const primary = countries[0];
  const primaryCity = info.cities.filter((c) => upper(c && c.countryCode) === primary)[0];

  return {
    userCountry,
    userLabel: (geo && geo.cityName) || userCountry,
    breakdownCountry: primary,
    breakdownCityName: (primaryCity && primaryCity.cityName) || primary,
  };
};

// After the visitor corrects their home city, does that city clear the gate?
// Same rule: country only.
export const homeCityClearsGate = (
  homeCity: { countryCode?: string } | null | undefined,
  info: IBreakdownLike | null | undefined
): boolean => {
  const country = upper(homeCity && homeCity.countryCode);
  if (!country) return false;
  if (!info || !info.cities) return false;
  return info.cities.some((city) => upper(city && city.countryCode) === country);
};
